import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import {
  Brain, Download, Loader2, Mail, RefreshCw, ShieldCheck, Sparkles, TriangleAlert,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { blobErrorDetail } from '../../lib/blobError';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/reports/financial-analysis`;

const STATUS_STYLE = {
  vert: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  orange: 'bg-amber-100 text-amber-700 border-amber-200',
  rouge: 'bg-red-100 text-red-700 border-red-200',
};
const LEVEL_STYLE = {
  critique: 'text-red-700', eleve: 'text-orange-700', moyen: 'text-amber-700', faible: 'text-emerald-700',
};

const download = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  a.remove();
};

/**
 * Analyse financière IA (rapport d'analyste) + automatisation de l'envoi mensuel.
 * Visible uniquement pour le personnel interne autorisé (contrôle aussi côté backend).
 */
export const FinancialAiAnalysisCard = ({ periodParams, periodLabel, canConfigure }) => {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [schedule, setSchedule] = useState(null);
  const [savingSchedule, setSavingSchedule] = useState(false);
  const [sendingNow, setSendingNow] = useState(false);
  const [recipientsText, setRecipientsText] = useState('');

  const periodReady = Boolean(
    (periodParams?.year && periodParams?.month) || (periodParams?.start_date && periodParams?.end_date)
  );
  const paramsKey = JSON.stringify(periodParams || {});

  const fetchLatest = useCallback(async () => {
    if (!periodReady) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/latest`, { params: periodParams });
      setReport(data?.report ? data : null);
    } catch (err) {
      setReport(null);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey, periodReady]);

  useEffect(() => { fetchLatest(); }, [fetchLatest]);

  useEffect(() => {
    if (!canConfigure) return;
    axios.get(`${API}/schedule`)
      .then(({ data }) => {
        setSchedule(data);
        setRecipientsText((data?.recipients || []).join(', '));
      })
      .catch(() => setSchedule(null));
  }, [canConfigure]);

  const handleGenerate = async () => {
    if (!periodReady) return toast.error('Sélectionnez une période (mois ou plage de dates) pour lancer l\'analyse');
    setGenerating(true);
    try {
      const { data } = await axios.post(`${API}/generate`, null, { params: periodParams });
      setReport(data);
      toast.success('Analyse financière générée');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Analyse IA indisponible');
    } finally {
      setGenerating(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (!periodReady) return;
    setDownloading(true);
    try {
      const res = await axios.get(`${API}/download/pdf`, {
        params: periodParams, responseType: 'blob',
      });
      download(res.data, `Analyse_Financiere_${(periodLabel || '').replace(/\s+/g, '_')}.pdf`);
      toast.success('Rapport PDF téléchargé');
    } catch (err) {
      toast.error(await blobErrorDetail(err, 'Téléchargement impossible'));
    } finally {
      setDownloading(false);
    }
  };

  const saveSchedule = async (patch) => {
    setSavingSchedule(true);
    try {
      const payload = {
        ...schedule,
        ...patch,
        recipients: (patch?.recipients ?? recipientsText)
          .toString().split(/[,;\s]+/).map(s => s.trim()).filter(Boolean),
      };
      const { data } = await axios.put(`${API}/schedule`, payload);
      setSchedule(data);
      toast.success('Envoi automatique enregistré');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Enregistrement impossible');
    } finally {
      setSavingSchedule(false);
    }
  };

  const handleSendNow = async () => {
    setSendingNow(true);
    try {
      const { data } = await axios.post(`${API}/send-now`, null, {
        params: { year: periodParams?.year, month: periodParams?.month },
      });
      toast.success(`Envoyé à ${(data.recipients_ok || []).length} destinataire(s)`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Envoi impossible');
    } finally {
      setSendingNow(false);
    }
  };

  const r = report?.report;

  return (
    <Card className="mt-6 border-teal-200" data-testid="financial-ai-card">
      <CardHeader className="bg-gradient-to-r from-teal-50 to-emerald-50">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="flex items-start gap-3 min-w-0">
            <div className="p-2 rounded-lg bg-teal-100 shrink-0">
              <Brain className="w-5 h-5 text-teal-700" />
            </div>
            <div className="min-w-0">
              <CardTitle className="text-lg">Analyse financière IA — rapport d'analyste</CardTitle>
              <CardDescription>
                L'IA déjà configurée (cockpit finance) analyse le dossier du mois et produit
                dashboard, risques chiffrés, actions commerciales par client et feuille de route.
                Période analysée : <strong>{periodLabel || '—'}</strong>.
                <span className="inline-flex items-center gap-1 ml-1 text-teal-700">
                  <ShieldCheck className="w-3 h-3" /> réservé au personnel interne
                </span>
              </CardDescription>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 shrink-0">
            <Button
              variant="outline" onClick={handleGenerate} disabled={generating || !periodReady}
              data-testid="generate-financial-analysis"
            >
              {generating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
              {r ? 'Relancer l\'analyse' : 'Générer l\'analyse'}
            </Button>
            <Button
              className="bg-teal-600 hover:bg-teal-700" onClick={handleDownloadPdf}
              disabled={downloading || !periodReady} data-testid="download-financial-analysis-pdf"
            >
              {downloading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
              Rapport PDF
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent className="pt-5 space-y-5">
        {loading && (
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <RefreshCw className="w-4 h-4 animate-spin" /> Chargement de la dernière analyse…
          </div>
        )}

        {!loading && !r && (
          <p className="text-sm text-gray-500" data-testid="financial-analysis-empty">
            Aucune analyse enregistrée pour {periodLabel || 'cette période'}. Cliquez sur
            « Générer l'analyse » — le rapport sera ensuite téléchargeable en PDF.
          </p>
        )}

        {r && (
          <div className="space-y-5" data-testid="financial-analysis-content">
            <div className="rounded-lg bg-teal-50/60 border border-teal-100 p-4">
              <p className="text-[11px] uppercase tracking-wider font-semibold text-teal-700 mb-1">
                Synthèse exécutive · {report.periodLabel} · {report.aiProvider}/{report.aiModel}
              </p>
              <p className="text-sm text-gray-700 leading-relaxed">{r.synthese}</p>
            </div>

            {r.dashboard?.length > 0 && (
              <div className="grid gap-2 sm:grid-cols-2" data-testid="financial-analysis-dashboard">
                {r.dashboard.map((d, i) => (
                  <div key={i} className="border rounded-lg p-3 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-xs font-semibold text-gray-600 truncate">{d.indicateur}</p>
                      <Badge variant="outline" className={`text-[10px] ${STATUS_STYLE[d.statut] || ''}`}>
                        {d.statut}
                      </Badge>
                    </div>
                    <p className="text-base font-bold text-gray-900">{d.valeur}</p>
                    <p className="text-[11px] text-gray-500">{d.variation} · {d.lecture}</p>
                  </div>
                ))}
              </div>
            )}

            {r.actions_commerciales?.length > 0 && (
              <div data-testid="financial-analysis-actions">
                <p className="text-sm font-semibold text-gray-800 mb-2">
                  Actions commerciales à mener
                </p>
                <div className="space-y-2">
                  {r.actions_commerciales.map((a, i) => (
                    <div key={i} className="border rounded-lg p-3">
                      <div className="flex items-center gap-2">
                        <Badge className="bg-teal-600 text-[10px]">P{a.priorite}</Badge>
                        <p className="text-sm font-semibold text-gray-900 truncate">{a.client}</p>
                      </div>
                      <p className="text-xs text-gray-600 mt-1">{a.constat}</p>
                      <p className="text-sm text-gray-800 mt-1">→ {a.action}</p>
                      {a.argumentaire && (
                        <p className="text-xs italic text-gray-500 mt-1">« {a.argumentaire} »</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {r.risques?.length > 0 && (
              <div data-testid="financial-analysis-risks">
                <p className="text-sm font-semibold text-gray-800 mb-2">Risques prioritaires</p>
                <ul className="space-y-1.5">
                  {r.risques.map((x, i) => (
                    <li key={i} className="text-xs text-gray-700 flex gap-2">
                      <TriangleAlert className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${LEVEL_STYLE[x.niveau] || ''}`} />
                      <span>
                        <strong>{x.titre}</strong> — impact {x.impact_fcfa}. {x.mitigation}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {canConfigure && schedule && (
          <div className="border-t pt-4 space-y-3" data-testid="financial-analysis-schedule">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                  <Mail className="w-4 h-4 text-teal-700" /> Envoi automatique mensuel
                </p>
                <p className="text-xs text-gray-500">
                  Le {schedule.send_day} de chaque mois : rapport PDF + dossier Excel du mois écoulé,
                  envoyés aux destinataires ci-dessous.
                </p>
              </div>
              <Switch
                checked={!!schedule.enabled}
                onCheckedChange={(v) => saveSchedule({ enabled: v })}
                disabled={savingSchedule}
                data-testid="financial-analysis-schedule-toggle"
              />
            </div>
            <div className="grid gap-3 sm:grid-cols-[1fr_auto_auto]">
              <div className="min-w-0">
                <Label className="text-xs">Destinataires (séparés par des virgules)</Label>
                <Input
                  value={recipientsText}
                  onChange={(e) => setRecipientsText(e.target.value)}
                  placeholder="direction@baobab.ci, finance@baobab.ci"
                  className="h-9 text-sm"
                  data-testid="financial-analysis-recipients"
                />
              </div>
              <div>
                <Label className="text-xs">Jour</Label>
                <Input
                  type="number" min={1} max={28} value={schedule.send_day}
                  onChange={(e) => setSchedule({ ...schedule, send_day: Number(e.target.value) })}
                  className="h-9 text-sm w-20"
                  data-testid="financial-analysis-send-day"
                />
              </div>
              <div className="flex items-end gap-2">
                <Button
                  variant="outline" onClick={() => saveSchedule({})} disabled={savingSchedule}
                  data-testid="financial-analysis-save-schedule"
                >
                  {savingSchedule ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Enregistrer
                </Button>
                <Button
                  variant="outline" onClick={handleSendNow} disabled={sendingNow}
                  data-testid="financial-analysis-send-now"
                >
                  {sendingNow ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}
                  Envoyer maintenant
                </Button>
              </div>
            </div>
            <label className="flex items-center gap-2 text-xs text-gray-600">
              <input
                type="checkbox"
                checked={!!schedule.send_commercial_digests}
                onChange={(e) => saveSchedule({ send_commercial_digests: e.target.checked })}
                data-testid="financial-analysis-commercial-digests"
              />
              Envoyer aussi à chaque commercial l'analyse de son portefeuille (sans la trésorerie d'entreprise)
            </label>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default FinancialAiAnalysisCard;
